<template>
  <div class="ad-page">
    <!-- page header rail. same construction as the main dashboard's
         .mdash-rail (accent edge, gradient band, title left / actions right),
         so the alerts landing surface reads as the same family. it also
         replaces the old hardcoded blue gradient band, which had no midnight
         counterpart and so stayed pale-on-dark there. -->
    <div class="ad-rail ad-rail--flush">
      <h3 class="ad-rail-title">Alerts Dashboard <FiFavStar /></h3>
    </div>

    <!-- filters in the ORM language: one labelled row matching the ops list's
         .od-filter-row -- range chips up front, then a bespoke From/To window
         for admins. the native date inputs auto-apply on change (same as the
         ops list), so the old explicit Search button is gone. -->
    <div class="ad-filter-row">
      <div class="ad-chip-group" role="group" aria-label="Alerts date range">
        <span class="ad-chip-group-label">Range</span>
        <button
          v-for="option in dateRangeOptions"
          :key="option"
          type="button"
          class="ad-chip"
          :class="{ 'ad-chip--active': isActiveRange(option) }"
          :aria-pressed="isActiveRange(option) ? 'true' : 'false'"
          :disabled="loading"
          @click="set_days_range(option)"
        >{{ option }}d</button>
      </div>
      <!-- back arrows flank the from date, forward arrows the to date; the
           cluster travels as one block so a tight row never orphans the arrows -->
      <div v-if="isEffectiveAdmin" class="ad-filter-dates">
        <span class="ad-filter-dateunit">
          <b-button-group class="toolbar-date-shifts">
            <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn" title="Shift back" v-on:click.prevent="shift_date(-selectedDayCount)"><i class="fa fa-angle-double-left" aria-hidden="true"></i></b-button>
            <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn" title="Previous day" v-on:click.prevent="shift_date(-1)"><i class="fa fa-angle-left" aria-hidden="true"></i></b-button>
          </b-button-group>
          <span class="ad-filter-field">
            <span class="ad-chip-group-label">From</span>
            <b-form-input v-model="fromDateIso" type="date" size="sm" class="ad-filter-input" @change="load_data()"/>
          </span>
        </span>
        <span class="ad-filter-dateunit">
          <span class="ad-filter-field">
            <span class="ad-chip-group-label">To</span>
            <b-form-input v-model="toDateIso" type="date" size="sm" class="ad-filter-input" @change="load_data()"/>
          </span>
          <b-button-group class="toolbar-date-shifts">
            <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn" title="Next day" v-on:click.prevent="shift_date(1)"><i class="fa fa-angle-right" aria-hidden="true"></i></b-button>
            <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn" title="Shift forward" v-on:click.prevent="shift_date(selectedDayCount)"><i class="fa fa-angle-double-right" aria-hidden="true"></i></b-button>
          </b-button-group>
        </span>
      </div>
    </div>

    <!-- first load only: on a range change the previous render holds at
         reduced opacity instead of flashing back to a spinner -->
    <div v-if="loading && !initialLoaded" class="text-center" style="margin-top: 40px;">
      <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>

    <div v-if="initialLoaded" class="ad-content" :class="{ 'ad-stale': loading }">
      <div v-if="summary.truncated" class="ad-truncation-note">
        <i class="fa fa-info-circle" aria-hidden="true"></i>
        Showing the {{ (summary.results_limit || 0).toLocaleString() }} most recent alerts for this range, so
        totals and charts below reflect only these. Narrow the date range for complete results.
      </div>

      <div class="ad-grid">
        <!-- summary tiles: the same dw-stat-tile language the dashboard's
             Alerts Summary widget uses, so the two agree on sight -->
        <div class="ad-card ad-card--full">
          <div class="ad-card-header">
            <span class="ad-card-title"><i class="fa fa-bell" aria-hidden="true"></i> Summary</span>
            <span class="ad-range-badge">{{ selectedDayCount }}d</span>
          </div>
          <div class="ad-card-body">
            <div class="dw-stat-grid">
              <div v-if="summary.total_cnt > 0" class="dw-stat-tile">
                <div class="dw-stat-tile-label">Total Alerts</div>
                <div class="dw-stat-tile-value">{{ summary.total_cnt }}</div>
              </div>
              <div v-if="summary.email_cnt > 0" class="dw-stat-tile">
                <div class="dw-stat-tile-label">Emails Sent</div>
                <div class="dw-stat-tile-value">{{ summary.email_cnt }}</div>
              </div>
              <div v-if="summary.sms_cnt > 0" class="dw-stat-tile">
                <div class="dw-stat-tile-label">SMS Sent</div>
                <div class="dw-stat-tile-value">{{ summary.sms_cnt }}</div>
              </div>
              <div v-if="summary.voice_cnt > 0" class="dw-stat-tile">
                <div class="dw-stat-tile-label">Voice Sent</div>
                <div class="dw-stat-tile-value">{{ summary.voice_cnt }}</div>
              </div>
              <div v-if="summary.sms_cnt > 0" class="dw-stat-tile">
                <div class="dw-stat-tile-label">SMS Cost</div>
                <div class="dw-stat-tile-value">${{ summary.sms_cost }}</div>
              </div>
              <div class="dw-stat-tile">
                <div class="dw-stat-tile-label">Success Rate</div>
                <div class="dw-stat-tile-value" :class="successRateClass">{{ successRatePct != null ? successRatePct + '%' : 'n/a' }}</div>
              </div>
            </div>
          </div>
        </div>

        <template v-if="items_full.length">
          <!-- alerts over time, by type. shared svg timeline (thin stacked
               columns, crosshair tooltip, table twin) with the validated
               type palette, themed per mode -->
          <div class="ad-card">
            <div class="ad-card-header">
              <span class="ad-card-title"><i class="fa fa-bar-chart" aria-hidden="true"></i> Alerts By Type</span>
            </div>
            <div class="ad-card-body">
              <fi-timeline-chart
                v-if="alertsOverTimeTimeline"
                :rows="alertsOverTimeTimeline.rows"
                :series="alertsOverTimeTimeline.series"
                :height="300"
                chart-label="Alerts per day by type"
              />
              <div v-else class="ad-chart-empty">No data available</div>
            </div>
          </div>

          <!-- ranked list, not an axis chart: label / thin bar / count, so
               long type names never truncate against a chart gutter and the
               value is always visible as text -->
          <div class="ad-card">
            <div class="ad-card-header">
              <span class="ad-card-title"><i class="fa fa-list-ol" aria-hidden="true"></i> Alert Counts by Type</span>
            </div>
            <div class="ad-card-body">
              <fi-rank-rows v-if="alertsByTypeRank.length" :rows="alertsByTypeRank" :color="accentBar"/>
              <div v-else class="ad-chart-empty">No data available</div>
            </div>
          </div>

          <!-- delivery outcome as one chart per channel: a single 4-series
               success/fail stack cannot seat its hues CVD-safely side by
               side, and per-channel small multiples read faster anyway.
               success/fail wear the validated teal/red status pair. -->
          <div v-if="emailDeliveryTimeline" class="ad-card">
            <div class="ad-card-header">
              <span class="ad-card-title"><i class="fa fa-envelope-o" aria-hidden="true"></i> Email Delivery</span>
            </div>
            <div class="ad-card-body">
              <fi-timeline-chart
                :rows="emailDeliveryTimeline.rows"
                :series="emailDeliveryTimeline.series"
                :height="300"
                chart-label="Email deliveries per day, success and fail"
              />
            </div>
          </div>

          <div v-if="smsDeliveryTimeline" class="ad-card">
            <div class="ad-card-header">
              <span class="ad-card-title"><i class="fa fa-mobile" aria-hidden="true"></i> SMS Delivery</span>
            </div>
            <div class="ad-card-body">
              <fi-timeline-chart
                :rows="smsDeliveryTimeline.rows"
                :series="smsDeliveryTimeline.series"
                :height="300"
                chart-label="SMS deliveries per day, success and fail"
              />
            </div>
          </div>

          <!-- failures only, by distinct reason -->
          <div v-if="failureReasonsTimeline" class="ad-card">
            <div class="ad-card-header">
              <span class="ad-card-title"><i class="fa fa-exclamation-triangle" aria-hidden="true"></i> Delivery Failure Reasons</span>
            </div>
            <div class="ad-card-body">
              <fi-timeline-chart
                :rows="failureReasonsTimeline.rows"
                :series="failureReasonsTimeline.series"
                :height="300"
                chart-label="Delivery failures per day by reason"
              />
            </div>
          </div>

          <!-- confirmed vs unconfirmed driver trip notifications -->
          <div v-if="driverConfirmationEnabled && confirmationTimeline" class="ad-card">
            <div class="ad-card-header">
              <span class="ad-card-title"><i class="fa fa-check-circle" aria-hidden="true"></i> Driver Trip Notification Confirmations</span>
            </div>
            <div class="ad-card-body">
              <fi-timeline-chart
                :rows="confirmationTimeline.rows"
                :series="confirmationTimeline.series"
                :height="300"
                chart-label="Driver trip notification confirmations per day"
              />
            </div>
          </div>

          <!-- breakdown: top drivers as ranked rows; names get the full row
               width instead of a 45% chart gutter, so these no longer need
               to claim the full grid width -->
          <div v-if="lateByDriverRank.length" class="ad-card">
            <div class="ad-card-header">
              <span class="ad-card-title"><i class="fa fa-clock-o" aria-hidden="true"></i> Most Late Alerts by Driver</span>
            </div>
            <div class="ad-card-body">
              <fi-rank-rows :rows="lateByDriverRank" :color="accentBar"/>
            </div>
          </div>

          <div v-if="dvirByDriverRank.length" class="ad-card">
            <div class="ad-card-header">
              <span class="ad-card-title"><i class="fa fa-clipboard" aria-hidden="true"></i> Most DVIR Alerts by Driver</span>
            </div>
            <div class="ad-card-body">
              <fi-rank-rows :rows="dvirByDriverRank" :color="accentBar"/>
            </div>
          </div>

          <div class="ad-card ad-card--full">
            <div class="ad-card-header">
              <span class="ad-card-title"><i class="fa fa-exclamation-circle" aria-hidden="true"></i> Recent Delivery Errors</span>
            </div>
            <div class="ad-card-body">
              <div v-if="recentFailures.length" class="dw-table-wrap">
                <table class="dw-table">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Alert Type</th>
                      <th>Booking ID</th>
                      <th>Vehicle ID</th>
                      <th>Recipient</th>
                      <th>Phone / Email</th>
                      <th>Reason</th>
                      <th>Message Preview</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="f in recentFailures" :key="f.key">
                      <td class="ad-failure-time">{{f.timeLabel}}</td>
                      <td>{{f.alert_type}}</td>
                      <td>{{f.booking_id}}</td>
                      <td>{{f.vehicle_id}}</td>
                      <td>{{f.recipient}}</td>
                      <td>{{f.contact}}</td>
                      <td class="ad-failure-reason">{{f.reason}}</td>
                      <td class="ad-failure-preview">{{f.preview}}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="dw-empty">No delivery failures in this range.</div>
            </div>
          </div>
        </template>
      </div>

      <div v-if="!items_full.length" class="ad-page-empty">
        No alerts found for this date range.
      </div>
    </div>
  </div>
</template>

<script>
import FiTimelineChart from './charts/fi_timeline_chart.vue'
import FiRankRows from './charts/fi_rank_rows.vue'
import { get_alerts } from '../networking/apis'
import { mapState } from 'vuex'
import moment from 'moment'
import { justDate } from '@/helpers/dates'
import {
  TYPE_COLOR_PALETTE, TYPE_COLOR_PALETTE_DARK,
  OTHER_TYPE_COLOR, OTHER_TYPE_COLOR_DARK,
  alertTypesOverTime, capSeriesColumns, timelineFromGviz,
} from '../utils/alerts_analytics'

// the full page's charts share the compact widget's CVD-validated palette and
// its 8-series + "Other" cap (see utils/alerts_analytics.js), so the main
// dashboard's Alerts By Type card and this page color the same data the same
// way. the old 20-color named-css list gave every type its own series, but at
// this card width the legend paginated and neighboring hues blurred together.
const MAX_TYPE_SERIES = 8

// success/fail status pair for the per-channel delivery charts, palette-
// validated per theme (light 9.5 protan; dark 6.6 sits in the warn band,
// legal with the secondary encoding the timeline ships: 2px surface gaps,
// legend, tooltip, and the table twin)
const OUTCOME_COLORS = { ok: '#0b7f56', fail: '#d62f2f' }
const OUTCOME_COLORS_DARK = { ok: '#27a075', fail: '#e46666' }
// confirmed teal / pending amber (validated both modes)
const CONFIRM_COLORS = { yes: '#0b7f56', no: '#d39410' }
const CONFIRM_COLORS_DARK = { yes: '#27a075', no: '#bd8524' }

const DATE_RANGE_OPTIONS = [1, 7, 30, 90]
const DEFAULT_DATE_RANGE = 30

export default {
  name: 'alerts_dashboard',
  components: {
    FiTimelineChart,
    FiRankRows,
  },
  data() {
    return {
      loading: true,
      initialLoaded: false,
      items_full: [],
      summary: {},
      details_fromDate: justDate(moment().subtract(DEFAULT_DATE_RANGE, 'days')),
      details_toDate: justDate(moment()),
    }
  },
  async created() {
    this.load_data()
  },
  methods: {
    async load_data() {
      this.loading = true
      try {
        // mode=dashboard: slim per-record payload (fat fields ride failed
        // records only) with the full summary, so the cap sits at 10k rows
        // instead of the fat endpoint's 2k before the 6MB lambda ceiling
        const { value: { data = [] } } = await get_alerts(
          this.user.tenant,
          '',
          moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD'),
          moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD'),
          'dashboard'
        )
        this.summary = data.summary || {}
        if (this.summary.timezone) moment.tz.setDefault(this.summary.timezone)
        this.items_full = data.alerts || []
      } catch (e) {
        this.$emit('showAlert', 'error retrieving data', 'danger', 5000)
      }
      this.loading = false
      this.initialLoaded = true
    },
    set_days_range(option) {
      this.details_fromDate = justDate(moment().subtract(option, 'days'))
      this.details_toDate = justDate(moment())
      this.load_data()
    },
    shift_date(shift) {
      this.details_fromDate = moment(this.details_fromDate, 'MM-DD-YYYY').add(shift, 'days').format('MM-DD-YYYY')
      this.details_toDate = moment(this.details_toDate, 'MM-DD-YYYY').add(shift, 'days').format('MM-DD-YYYY')
      this.load_data()
    },
    // active state is derived from the current from/to dates rather than a
    // separately tracked "selected option", so it stays correct whether the
    // range came from a quick button, the date pickers, or the shift arrows
    isActiveRange(option) {
      return this.details_fromDate === justDate(moment().subtract(option, 'days')) &&
        this.details_toDate === justDate(moment())
    },
    // mirrors the "Problems Only" definition on the Alert History page, so a
    // delivery counts as failed the same way in both places.
    getLatestEmailStatus(item) {
      if (!item.email_msg_status || !item.email_msg_status.length) return null
      return item.email_msg_status.reduce((a, b) => (new Date(a.datetime) > new Date(b.datetime) ? a : b))
    },
    isAlertFailed(item) {
      const emailStatus = this.getLatestEmailStatus(item)
      const hasEmailIssue = emailStatus && ['bounce', 'complaint'].includes(emailStatus.status.toLowerCase())
      const hasSmsIssue = item.provider_response && !item.provider_response.includes('Message has been accepted')
      return !!(hasEmailIssue || hasSmsIssue)
    },
    // null when the item never attempted that channel, so it's excluded from that channel's counts
    emailOutcome(item) {
      const emailStatus = this.getLatestEmailStatus(item)
      if (!emailStatus) return null
      return ['bounce', 'complaint'].includes(emailStatus.status.toLowerCase()) ? 'Email Fail' : 'Email Success'
    },
    smsOutcome(item) {
      if (!item.provider_response) return null
      return item.provider_response.includes('Message has been accepted') ? 'SMS Success' : 'SMS Fail'
    },
    // distinct reason strings an item can match - raw sms provider response text
    // (varies by carrier) plus the decoded email status; an item can match more
    // than one (e.g. both its sms and email attempts failed)
    failureReasonsFor(item) {
      const reasons = []
      if (item.provider_response && !item.provider_response.includes('Message has been accepted')) {
        reasons.push(this.normalizeSmsReason(item.provider_response))
      }
      const emailStatus = this.getLatestEmailStatus(item)
      if (emailStatus && ['bounce', 'complaint'].includes(emailStatus.status.toLowerCase())) {
        reasons.push(this.decodeEmailStatus(emailStatus.status))
      }
      return reasons
    },
    // "+12125864095 is opted out" varies per phone number, which would otherwise
    // fragment the opt-out cases into one series per number - strip the number so
    // they all group under a single "opted out" series
    normalizeSmsReason(raw) {
      const text = raw.trim()
      if (/opted out/i.test(text)) return 'opted out'
      return text
    },
    decodeEmailStatus(rawStatus) {
      const map = {
        send: 'sent',
        delivery: 'delivered',
        bounce: 'bounced',
        complaint: 'filed complaint',
        open: 'opened',
        click: 'clicked link',
      }
      return map[(rawStatus || '').toLowerCase()] || rawStatus
    },
    failureReason(item) {
      const parts = []
      if (item.provider_response && !item.provider_response.includes('Message has been accepted')) {
        parts.push(`sms: ${item.provider_response}`)
      }
      const emailStatus = this.getLatestEmailStatus(item)
      if (emailStatus && ['bounce', 'complaint'].includes(emailStatus.status.toLowerCase())) {
        parts.push(`email: ${this.decodeEmailStatus(emailStatus.status)}`)
      }
      return parts.join(' | ') || 'unknown'
    },
    // the phone number and/or email address that actually bounced/failed, as opposed
    // to failureReason() which describes why
    failureContact(item) {
      const parts = []
      if (item.provider_response && !item.provider_response.includes('Message has been accepted')) {
        // driver/leader recipients live in sms_drivers/sms_leaders rather than
        // the plain sms field, so sms_all_recipients is the one that's actually
        // populated for the driver-facing alert types (LateAlert, MissingDVIR, ...)
        const phone = item.sms_all_recipients || item.sms || item.sms_drivers || item.sms_leaders
        if (phone) parts.push(phone)
      }
      const emailStatus = this.getLatestEmailStatus(item)
      if (emailStatus && ['bounce', 'complaint'].includes(emailStatus.status.toLowerCase()) && item.email) {
        parts.push(item.email)
      }
      return parts.join(' | ')
    },
    firstLine(item) {
      const text = item.message || item.email_message || item.email_subject || ''
      const line = String(text).split('\n')[0].trim()
      return line.length > 80 ? line.slice(0, 80) + '…' : line
    },
    channelTimeline(okKey, failKey) {
      const byDay = this._deliveryByDay
      const days = Object.keys(byDay).sort()
      const rows = days.map(day => ({
        label: moment(day).format('MM/DD'),
        values: [byDay[day][okKey], byDay[day][failKey]],
      }))
      // a channel this tenant never attempted renders no card at all
      if (!rows.some(r => r.values[0] || r.values[1])) return null
      const c = this.outcomeColors
      return { series: [{ label: 'Success', color: c.ok }, { label: 'Fail', color: c.fail }], rows }
    },
    // shared adapter: series in volume order take the validated palette; a
    // folded "Other" column gets the grey (utils/alerts_analytics.js)
    gvizToTimeline(chartData) {
      return timelineFromGviz(chartData, this.typePalette, this.typeOtherColor)
    },
  },
  computed: {
    ...mapState({
      user: state => state.profile.user,
      viewAs: state => state.profile.view_as,
    }),
    isEffectiveAdmin() {
      return this.user.role === 'admin' && this.viewAs !== 'non-admin'
    },
    dateRangeOptions() {
      return DATE_RANGE_OPTIONS
    },
    // the native date inputs speak ISO; the rest of the page (and the api
    // calls) keep the legacy MM-DD-YYYY strings, so these adapt at the edge.
    // an empty value (the input's clear affordance) is ignored rather than
    // propagated -- this page always queries a bounded window.
    fromDateIso: {
      get() { return moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD') },
      set(v) { if (v) this.details_fromDate = moment(v, 'YYYY-MM-DD').format('MM-DD-YYYY') },
    },
    toDateIso: {
      get() { return moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD') },
      set(v) { if (v) this.details_toDate = moment(v, 'YYYY-MM-DD').format('MM-DD-YYYY') },
    },
    // length of the current from/to window in days, so the double-arrow
    // shift moves by "one more of these" instead of a fixed step
    selectedDayCount() {
      const diff = moment(this.details_toDate, 'MM-DD-YYYY').diff(moment(this.details_fromDate, 'MM-DD-YYYY'), 'days')
      return diff > 0 ? diff : 1
    },
    failedCount() {
      return this.items_full.filter(item => this.isAlertFailed(item)).length
    },
    successRatePct() {
      const total = this.items_full.length
      if (!total) return null
      return Math.round(((total - this.failedCount) / total) * 1000) / 10
    },
    // colors the tile's value rather than washing the whole tile, matching how
    // the dashboard widgets mark a good/bad figure
    successRateClass() {
      if (this.successRatePct == null) return ''
      if (this.successRatePct >= 95) return 'dw-stat-tile-value--good'
      if (this.successRatePct >= 80) return ''
      return 'dw-stat-tile-value--bad'
    },
    isMidnight() {
      return this.$store.state.profile.site_theme === 'midnight'
    },
    // dark mode is selected, never an automatic flip: both palettes are
    // separately validated against this page's card surface
    typePalette() {
      return this.isMidnight ? TYPE_COLOR_PALETTE_DARK : TYPE_COLOR_PALETTE
    },
    typeOtherColor() {
      return this.isMidnight ? OTHER_TYPE_COLOR_DARK : OTHER_TYPE_COLOR
    },
    outcomeColors() {
      return this.isMidnight ? OUTCOME_COLORS_DARK : OUTCOME_COLORS
    },
    // single-hue magnitude bars keep the page's own accent family
    accentBar() {
      return this.isMidnight ? '#d1841f' : '#c86e18'
    },
    alertsOverTimeChart() {
      return capSeriesColumns(alertTypesOverTime(this.items_full), MAX_TYPE_SERIES)
    },
    alertsOverTimeTimeline() {
      return this.gvizToTimeline(this.alertsOverTimeChart)
    },
    // per-channel success/fail rows for the delivery small multiples; null
    // when the channel was never attempted so its card doesn't render
    _deliveryByDay() {
      const byDay = {}
      this.items_full.forEach(item => {
        if (!item.processed_time) return
        const day = moment(item.processed_time).format('YYYY-MM-DD')
        if (!byDay[day]) byDay[day] = { emailOk: 0, emailFail: 0, smsOk: 0, smsFail: 0 }
        const email = this.emailOutcome(item)
        if (email) byDay[day][email === 'Email Success' ? 'emailOk' : 'emailFail']++
        const sms = this.smsOutcome(item)
        if (sms) byDay[day][sms === 'SMS Success' ? 'smsOk' : 'smsFail']++
      })
      return byDay
    },
    emailDeliveryTimeline() {
      return this.channelTimeline('emailOk', 'emailFail')
    },
    smsDeliveryTimeline() {
      return this.channelTimeline('smsOk', 'smsFail')
    },
    // confirmation status only means anything for tenants with driver trip
    // notification confirmation turned on - other tenants never populate
    // confirmed_time, so the chart would just be a solid "unconfirmed" bar
    driverConfirmationEnabled() {
      const features = this.user && this.user.features && this.user.features[this.user.tenant]
      return !!(features && features.driver_trip_notifications_confirmation_enabled)
    },
    driverConfirmationChart() {
      if (!this.driverConfirmationEnabled) return null
      const byDay = {}
      this.items_full
        .filter(item => item.alert_type === 'DriverTripNotification' && item.processed_time)
        .forEach(item => {
          const day = moment(item.processed_time).format('YYYY-MM-DD')
          if (!byDay[day]) byDay[day] = { Confirmed: 0, Unconfirmed: 0 }
          if (item.confirmed_time) byDay[day].Confirmed++
          else byDay[day].Unconfirmed++
        })
      const days = Object.keys(byDay).sort()
      if (!days.length) return null
      return [
        ['Date', 'Confirmed', 'Unconfirmed'],
        ...days.map(day => [moment(day).format('MM/DD'), byDay[day].Confirmed, byDay[day].Unconfirmed]),
      ]
    },
    confirmationTimeline() {
      const chart = this.driverConfirmationChart
      if (!chart || chart.length < 2) return null
      const c = this.isMidnight ? CONFIRM_COLORS_DARK : CONFIRM_COLORS
      return {
        series: [{ label: 'Confirmed', color: c.yes }, { label: 'Unconfirmed', color: c.no }],
        rows: chart.slice(1).map(r => ({ label: r[0], values: r.slice(1) })),
      }
    },
    deliveryFailureReasonsOverTimeChart() {
      if (!this.items_full.length) return null
      const byDay = {}
      const totalsByReason = {}
      this.items_full.forEach(item => {
        if (!item.processed_time) return
        const day = moment(item.processed_time).format('YYYY-MM-DD')
        this.failureReasonsFor(item).forEach(reason => {
          totalsByReason[reason] = (totalsByReason[reason] || 0) + 1
          if (!byDay[day]) byDay[day] = {}
          byDay[day][reason] = (byDay[day][reason] || 0) + 1
        })
      })
      const days = Object.keys(byDay).sort()
      if (!days.length) return null
      // top 6 reasons by volume + "Other", so the chart doesn't get cluttered
      // with the long tail of carrier-specific sms responses
      const sortedReasons = Object.entries(totalsByReason).sort((a, b) => b[1] - a[1])
      const hasOther = sortedReasons.length > 6
      const reasonList = (hasOther ? sortedReasons.slice(0, 6) : sortedReasons).map(([reason]) => reason)
      const cols = hasOther ? [...reasonList, 'Other'] : reasonList
      return [
        ['Date', ...cols],
        ...days.map(day => {
          const dayData = byDay[day] || {}
          const row = [moment(day).format('MM/DD'), ...reasonList.map(reason => dayData[reason] || 0)]
          if (hasOther) {
            const otherCount = Object.entries(dayData)
              .filter(([reason]) => !reasonList.includes(reason))
              .reduce((sum, [, count]) => sum + count, 0)
            row.push(otherCount)
          }
          return row
        }),
      ]
    },
    failureReasonsTimeline() {
      return this.gvizToTimeline(this.deliveryFailureReasonsOverTimeChart)
    },
    alertsByTypeRank() {
      const counts = this.summary.type_unit_counts || {}
      let entries = Object.entries(counts).map(([type, v]) => [type, v.count || 0]).sort((a, b) => b[1] - a[1])
      if (entries.length > 8) {
        const top = entries.slice(0, 7)
        const otherTotal = entries.slice(7).reduce((sum, [, count]) => sum + count, 0)
        entries = [...top, ['Other', otherTotal]]
      }
      return entries.map(([label, value]) => ({ label, value, tip: value.toLocaleString() + ' alerts' }))
    },
    recentFailures() {
      return this.items_full
        .filter(item => this.isAlertFailed(item))
        .slice()
        .sort((a, b) => new Date(b.processed_time) - new Date(a.processed_time))
        .slice(0, 5)
        .map(item => ({
          key: item.movement_vehicle_id || `${item.alert_type}-${item.processed_time}`,
          timeLabel: moment(item.processed_time).format('MM/DD h:mma'),
          alert_type: item.alert_type,
          booking_id: item.booking_id || '',
          vehicle_id: item.vehicle_id || item.movement_vehicle_id || '',
          recipient: item.sms_name || '',
          contact: this.failureContact(item),
          reason: this.failureReason(item),
          preview: this.firstLine(item),
        }))
    },
    // sms_name holds the driver's name for driver-facing alerts like LateAlert
    topLateAlertsByDriver() {
      const counts = {}
      this.items_full
        .filter(item => item.alert_type === 'LateAlert')
        .forEach(item => {
          const name = (item.sms_name || '').trim()
          if (!name) return
          counts[name] = (counts[name] || 0) + 1
        })
      return Object.entries(counts)
        .map(([driver, count]) => ({ driver, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10)
    },
    lateByDriverRank() {
      return this.topLateAlertsByDriver.map(r => ({
        label: r.driver, value: r.count, tip: r.count.toLocaleString() + ' late alerts',
      }))
    },
    // MissingDVIR-PRE / MissingDVIR-POST cover pre-trip and post-trip inspection alerts
    topDvirAlertsByDriver() {
      const counts = {}
      this.items_full
        .filter(item => (item.alert_type || '').startsWith('MissingDVIR'))
        .forEach(item => {
          const name = (item.sms_name || '').trim()
          if (!name) return
          counts[name] = (counts[name] || 0) + 1
        })
      return Object.entries(counts)
        .map(([driver, count]) => ({ driver, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10)
    },
    dvirByDriverRank() {
      return this.topDvirAlertsByDriver.map(r => ({
        label: r.driver, value: r.count, tip: r.count.toLocaleString() + ' DVIR alerts',
      }))
    },
  },
  watch: {
    user: {
      deep: true,
      handler() {
        this.load_data()
      },
    },
  },
}
</script>

<style scoped>
/* this page is the main dashboard's widget language applied to a full page:
   the rail from main_dashboard.vue, accent-framed cards with a tinted header
   band, and the shared dw-* tiles/tables from assets/css/dashboard-widgets.css
   inside them. the chrome rules below mirror main_dashboard.vue's scoped
   copies -- the same deliberate duplication the .mdash-range-* buttons
   already carry between these two files. */
/* gutters mirror ops_desk.vue's .od-root exactly (12px 20px 32px), so the two
   pages sit on the same margins. alerts_shell drops its own top margin and the
   32px left pad it reserves for the in-page nav whenever that nav is gone. */
.ad-page {
  /* one accent for the whole page: the Alerts section hex site_map.vue and
     the dashboard's alert widgets already use, so this reads as that
     section's own surface rather than a fourth palette. cards inherit it. */
  --mdash-accent: #c86e18;
  /* rail and card bleeds derive from this, so narrower breakpoints stay the
     same 1px off the edge instead of snapping to a wider inset */
  --ad-gutter: 20px;
  padding: 12px var(--ad-gutter) 32px;
}

@media (max-width: 720px) {
  .ad-page {
    --ad-gutter: 16px;
    padding: 12px var(--ad-gutter) 24px;
  }
}

.ad-rail {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  background: var(--fi-ops-rail-bg);
  border-left: 3px solid var(--fi-rail-accent);
  /* square rail edge, soft right side -- .od-rail's treatment */
  border-radius: 0 6px 6px 0;
  padding: 12px 16px;
  margin-bottom: 14px;
}

/* the .od-rail--flush recipe: bleed into the page gutters so the title text
   (3px accent + 16px padding = 19px in) lands on the same x as the filter
   labels and card edges below it */
.ad-rail--flush { margin: -11px calc(1px - var(--ad-gutter)) 14px; }

/* flex, not just a heading: App.vue's .container-fluid sets text-align:center
   and every page inherits it, so a plain h3 renders centred in the band */
.ad-rail-title {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  padding: 0;
  font-size: 22px;
  line-height: 1.2;
  font-weight: 700;
  letter-spacing: -0.1px;
  color: var(--fi-text);
}

/* ── filters: the ops_desk chip + filter-row language ──────────────── */
.ad-chip-group {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.ad-chip-group-label {
  font-size: 10.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--fi-text-muted);
  margin-right: 2px;
  align-self: center;
}

.ad-chip {
  background: var(--fi-surface);
  border: 1px solid var(--fi-line-strong);
  border-radius: var(--fi-radius-pill);
  padding: 4px 12px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--fi-text-muted);
  cursor: pointer;
  transition: background .12s, border-color .12s, color .12s;
}

.ad-chip:hover:not(:disabled):not(.ad-chip--active) {
  background: var(--fi-surface-sunken);
  color: var(--fi-text);
}

.ad-chip:disabled {
  cursor: default;
  opacity: 0.6;
}

.ad-chip--active {
  background: var(--fi-ops-accent);
  border-color: var(--fi-ops-accent);
  color: #fff;
}

.ad-filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--fi-line);
  margin-bottom: 14px;
  font-size: 13px;
}

.ad-filter-field {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

/* one-line date cluster; only true mobile splits it, and then each half
   keeps its flanking arrows */
.ad-filter-dates {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 8px 10px;
}

.ad-filter-dateunit {
  display: inline-flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 8px;
}

@media (max-width: 600px) {
  .ad-filter-dates {
    flex-wrap: wrap;
  }
}

.ad-filter-input {
  width: 150px;
  font-size: 13px;
}

.toolbar-date-shift-btn {
  padding: 0.15rem 0.35rem;
  line-height: 1;
  min-width: 1.65rem;
}

.toolbar-date-shift-btn .fa {
  font-size: 13px;
  font-weight: bold;
  vertical-align: middle;
}

.ad-truncation-note {
  text-align: center;
  font-size: 13px;
  color: var(--fi-text-muted);
  background: var(--fi-surface-sunken);
  border: 1px solid var(--fi-line);
  border-radius: var(--fi-radius-md);
  padding: 6px 12px;
  margin: 0 0 14px;
}

.ad-page-empty {
  text-align: center;
  color: var(--fi-text-muted);
  font-size: 15px;
  margin-top: 40px;
}

/* one grid for every card on the page, rather than the old sequence of
   independent chart rows: cards that want the full width claim it with
   --full, and everything else pairs up two across on a desktop. */
.ad-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(440px, 1fr));
  gap: 14px;
  align-items: stretch;
}

/* the cards take the same reach into the gutters that .od-table-wrap gives
   the ops list, so the page's content spans the rail edge to edge instead of
   sitting inside it */
.ad-grid,
.ad-truncation-note {
  margin-left: calc(1px - var(--ad-gutter));
  margin-right: calc(1px - var(--ad-gutter));
}

/* 1 / -1 rather than span 2: a `span 2` card overflows the page once the
   auto-fit track count drops to one */
.ad-card--full {
  grid-column: 1 / -1;
}

/* mirrors site_map.vue's section-card banding, exactly as .mdash-card does:
   a top + left accent bar framing a soft-tinted header and body */
.ad-card {
  display: flex;
  flex-direction: column;
  background: var(--fi-surface);
  border: 2px solid color-mix(in srgb, var(--mdash-accent) 28%, var(--fi-line));
  border-radius: 16px;
  box-shadow:
    0 4px 22px color-mix(in srgb, var(--mdash-accent) 14%, transparent),
    0 1px 4px rgba(20, 40, 60, 0.06);
  position: relative;
  overflow: hidden;
}

.ad-card::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 6px;
  background: linear-gradient(
    90deg,
    var(--mdash-accent),
    color-mix(in srgb, var(--mdash-accent) 55%, white)
  );
  z-index: 3;
}

.ad-card::after {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 6px;
  background: linear-gradient(
    180deg,
    var(--mdash-accent),
    color-mix(in srgb, var(--mdash-accent) 70%, white)
  );
  z-index: 1;
}

.ad-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 14px 16px 12px 20px;
  background: color-mix(in srgb, var(--mdash-accent) 14%, var(--fi-surface));
  border-bottom: 2px solid color-mix(in srgb, var(--mdash-accent) 22%, var(--fi-line));
  position: relative;
  z-index: 2;
}

.ad-card-title {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 1 auto;
  min-width: 0;
  font-size: 15px;
  font-weight: 700;
  color: color-mix(in srgb, var(--mdash-accent) 65%, var(--fi-text));
}

.ad-card-title .fa {
  color: var(--fi-brand);
}

.ad-card-body {
  /* mirrors the derivation dashboard-widgets.css puts on .mdash-card-body, so
     the shared dw-* tiles and tables tint from this page's accent the same way
     they do inside a dashboard widget */
  --dw-accent: var(--mdash-accent, var(--fi-rail-accent));
  --dw-tint: color-mix(in srgb, var(--dw-accent) 8%, var(--fi-surface));
  --dw-tint-strong: color-mix(in srgb, var(--dw-accent) 15%, var(--fi-surface));
  --dw-edge: color-mix(in srgb, var(--dw-accent) 26%, var(--fi-line));
  flex: 1;
  padding: 14px 16px 16px 20px;
  background: color-mix(in srgb, var(--mdash-accent) 6%, var(--fi-surface));
  position: relative;
  z-index: 2;
}

/* passive window chip on the summary card, sibling geometry with the
   dashboard's .mdash-range-badge */
.ad-range-badge {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  padding: 3px 9px;
  border-radius: var(--fi-radius-pill);
  color: color-mix(in srgb, var(--mdash-accent) 35%, var(--fi-text-muted));
  background: color-mix(in srgb, var(--mdash-accent) 6%, var(--fi-surface));
  border: 1px solid color-mix(in srgb, var(--mdash-accent) 20%, var(--fi-line));
  white-space: nowrap;
  flex-shrink: 0;
}

/* refetch keeps the frame: the previous render dims instead of a spinner */
.ad-stale {
  opacity: 0.55;
  pointer-events: none;
  transition: opacity .15s;
}

.ad-chart-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--fi-text-muted);
  font-size: 13px;
}

/* the reason and preview are the two cells anyone actually reads, and
   .dw-table's nowrap + 160px cap clipped both; let them wrap instead */
.ad-failure-time {
  white-space: nowrap;
  color: var(--fi-text-muted);
}

/* the raw --fi-danger red only reaches 2.2:1 on midnight's card body. mixing
   it toward --fi-text deepens it in the light themes and lightens it in
   midnight, so one declaration clears the bar in both directions (same trick
   dashboard-widgets.css uses for .dw-chip-neutral). */
.ad-failure-reason {
  color: color-mix(in srgb, var(--fi-danger) 45%, var(--fi-text));
  white-space: normal;
  max-width: 240px;
}

.ad-failure-preview {
  color: var(--fi-text-muted);
  font-style: italic;
  white-space: normal;
  max-width: 320px;
}

/* phones: the auto-fit track floor is wider than the screen, so name a single
   column outright. keep this block last -- a media query adds no specificity,
   so it has to win by order. */
@media (max-width: 600px) {
  .ad-page {
    --ad-gutter: 12px;
    padding: 12px var(--ad-gutter) 24px;
  }

  .ad-grid {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .ad-card {
    border-radius: 12px;
  }

  .ad-card-header {
    padding: 11px 12px 10px 15px;
  }

  .ad-card-body {
    padding: 12px 12px 13px 15px;
  }

  /* mobile keeps the stock rail chrome, same as .od-rail does */
  .ad-rail {
    padding: 10px 12px;
    margin-bottom: 10px;
  }

  .ad-rail-title {
    font-size: 19px;
  }

  .ad-filter-input {
    width: 118px;
  }
}
</style>
